import { BehaviorSubject, Observable, finalize, map } from 'rxjs'
import { BookingCommunicationMessage } from '@app/domain/BookingCommunicationMessage'
import {
    BookingMessageDTO,
    MessageThreadDTO,
    SendBookingMessageRequestDTO,
    UnreadMessageCountDTO,
} from '@app/dtos/BookingCommunicationDTO'
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { MessageThread } from '@app/domain/MessageThread'
import { DateTime } from 'luxon'
import { environment } from '@environments/environment'

@Injectable({ providedIn: 'root' })
export class BookingCommunicationService {

    private _loadingThreads$ = new BehaviorSubject<boolean>(false)
    loadingThreads$ = this._loadingThreads$.asObservable()

    private _loadingMessages$ = new BehaviorSubject<boolean>(false)
    loadingMessages$ = this._loadingMessages$.asObservable()

    private _sendingMessage$ = new BehaviorSubject<boolean>(false)
    sendingMessage$ = this._sendingMessage$.asObservable()

    constructor(
        private http: HttpClient
    ) { }

    getMessageThreads(
        organisationId: string,
        businessId: string,
        venueId: string
    ): Observable<MessageThread[]> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/messages/threads'
        const url = new URL(path, environment.apiBaseURL)
        this._loadingThreads$.next(true)
        return this.http.get<MessageThreadDTO[]>(url.toString())
            .pipe(
                finalize(() => this._loadingThreads$.next(false)),
                map(dtos => dtos.map(dto => this.adaptThreadDto(dto)))
            )
    }

    getUnreadMessageCount(
        organisationId: string,
        businessId: string,
        venueId: string
    ): Observable<number> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/messages/unread-count'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<UnreadMessageCountDTO>(url.toString())
            .pipe(map(dto => dto.unreadMessageCount))
    }

    getMessagesForBooking(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string
    ): Observable<BookingCommunicationMessage[]> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/messages'
        const url = new URL(path, environment.apiBaseURL)
        this._loadingMessages$.next(true)
        return this.http.get<BookingMessageDTO[]>(url.toString())
            .pipe(
                finalize(() => this._loadingMessages$.next(false)),
                map(dtos => dtos.map(dto => this.adaptMessageDto(dto)))
            )
    }

    sendMessage(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string,
        messageBody: string,
        subject?: string | null
    ): Observable<BookingCommunicationMessage> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/messages'
        const url = new URL(path, environment.apiBaseURL)
        const body: SendBookingMessageRequestDTO = {
            messageBody,
            subject: subject ?? null,
        }
        this._sendingMessage$.next(true)
        return this.http.post<BookingMessageDTO>(url.toString(), body)
            .pipe(
                finalize(() => this._sendingMessage$.next(false)),
                map(dto => this.adaptMessageDto(dto))
            )
    }

    markMessagesReadForBooking(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string
    ): Observable<void> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/messages/read'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.patch<void>(url.toString(), {})
    }

    private adaptThreadDto(dto: MessageThreadDTO): MessageThread {
        return new MessageThread(
            dto.bookingId,
            dto.customerName,
            dto.customerEmail,
            DateTime.fromISO(dto.bookingDate, { zone: environment.assumedVenueTimeZone }).toJSDate(),
            dto.partySize,
            dto.lastMessageBody,
            dto.lastMessageSenderType,
            DateTime.fromISO(dto.lastMessageDate, { zone: environment.assumedVenueTimeZone }).toJSDate(),
            dto.messageCount,
            dto.unreadMessageCount,
            dto.lastCustomerMessageReadAt
                ? DateTime.fromISO(dto.lastCustomerMessageReadAt, { zone: environment.assumedVenueTimeZone }).toJSDate()
                : null,
        )
    }

    private adaptMessageDto(dto: BookingMessageDTO): BookingCommunicationMessage {
        return new BookingCommunicationMessage(
            dto.id,
            dto.senderType,
            dto.subject,
            dto.messageBody,
            DateTime.fromISO(dto.dateCreated, { zone: environment.assumedVenueTimeZone }).toJSDate(),
            dto.readAt ? DateTime.fromISO(dto.readAt, { zone: environment.assumedVenueTimeZone }).toJSDate() : null,
        )
    }
}
